import React, { useState, useEffect } from 'react';
import { 
  Package, Coffee, AlertCircle, AlertTriangle, CheckCircle, 
  Trash2, Eye 
} from 'lucide-react';
import { articulosService, UPLOADS_BASE_URL } from '../../services/api';

/**
 * Componente para mostrar un artículo con su imagen
 * Vía de entrega: select requerido con opciones Nacional / Internacional
 */
const ArticuloItem = ({ articulo, index, onUpdate, onRemove, isViewMode = false }) => {
  const [imagenUrl, setImagenUrl] = useState(null);
  const [cargandoImagen, setCargandoImagen] = useState(false);

  const esExterno = !articulo.id_articulo;
  const nombreArticulo = articulo.nombre || articulo.observacion || 'Artículo externo';
  const esServicio = articulo.tipo === 'servicio';
  const stockActual = articulo.stock || articulo.stock_actual || 0;
  const stockInsuficiente = !esServicio && !esExterno && articulo.cantidad > stockActual;
  const stockBajo = !esServicio && !esExterno && stockActual <= (articulo.stock_minimo || 0);

  useEffect(() => {
    const cargarImagen = async () => {
      if (!articulo.id_articulo || esExterno) return;
      setCargandoImagen(true);
      try {
        const response = await articulosService.getById(articulo.id_articulo);
        if (response.data?.imagen_principal) {
          setImagenUrl(`${UPLOADS_BASE_URL}${response.data.imagen_principal}`);
        }
      } catch (error) {
        console.error('Error al cargar imagen del artículo:', error);
      } finally {
        setCargandoImagen(false);
      }
    };
    cargarImagen();
  }, [articulo.id_articulo]);

  // 🔍 DEBUG — log imposible de no ver
  if (isViewMode) {
    console.error('══════════════════════════════════');
    console.error('🎯 ArticuloItem (VIEW MODE) props:');
    console.error('   nombre:', articulo.nombre, '| nombreArticulo:', nombreArticulo);
    console.error('   id_articulo:', articulo.id_articulo, '| esExterno:', esExterno);
    console.error('   via:', JSON.stringify(articulo.via), '| tipo:', typeof articulo.via);
    console.error('   via === nacional:', articulo.via === 'nacional');
    console.error('   trim+lower:', (articulo.via||'').trim().toLowerCase());
    console.error('   tiempo_entrega:', articulo.tiempo_entrega);
    console.error('   observacion:', articulo.observacion);
    console.error('══════════════════════════════════');
  }

  return (
    <div 
      className={`border-2 rounded-lg overflow-hidden ${
        esServicio ? 'border-purple-300 bg-purple-50' :
        stockInsuficiente ? 'border-red-300 bg-red-50' :
        stockBajo ? 'border-yellow-300 bg-yellow-50' :
        'border-green-300 bg-green-50'
      }`}
    >
      <div className="grid grid-cols-1 md:grid-cols-4 gap-0">

        {/* ── Columna izquierda: Imagen ─────────────────── */}
        <div className="md:col-span-1 relative group bg-white">
          {cargandoImagen ? (
            <div className="aspect-square flex items-center justify-center bg-gray-100">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent"></div>
            </div>
          ) : imagenUrl ? (
            <>
              <img
                src={imagenUrl}
                alt={nombreArticulo}
                className="w-full h-full object-cover aspect-square"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://via.placeholder.com/200x200/f3f4f6/9ca3af?text=Sin+Imagen';
                }}
              />
              <a
                href={imagenUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-50 flex items-center justify-center opacity-0 hover:opacity-100 transition-all duration-200"
              >
                <div className="bg-white rounded-full p-2 shadow-lg">
                  <Eye size={20} className="text-blue-600" />
                </div>
              </a>
            </>
          ) : (
            <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-200 flex flex-col items-center justify-center">
              {esExterno ? (
                <><Package size={48} className="text-orange-400 mb-2" /><span className="text-xs font-semibold text-orange-600">EXTERNO</span></>
              ) : esServicio ? (
                <><Coffee size={48} className="text-purple-400 mb-2" /><span className="text-xs font-semibold text-purple-600">SERVICIO</span></>
              ) : (
                <><Package size={48} className="text-gray-400 mb-2" /><span className="text-xs font-semibold text-gray-500">SIN IMAGEN</span></>
              )}
            </div>
          )}
        </div>

        {/* ── Columna derecha: Información ──────────────── */}
        <div className="md:col-span-3 p-4">

          {/* Header: Nombre + badge tipo + botón eliminar */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <h5 className="font-bold text-lg text-gray-900">{nombreArticulo}</h5>
              {esExterno && (
                <span className="px-2 py-1 bg-orange-200 text-orange-900 text-xs font-bold rounded-full flex items-center gap-1">
                  <Package size={12} /> EXTERNO
                </span>
              )}
              {esServicio && (
                <span className="px-2 py-1 bg-purple-200 text-purple-900 text-xs font-bold rounded-full flex items-center gap-1">
                  <Coffee size={12} /> SERVICIO
                </span>
              )}
            </div>
            {!isViewMode && onRemove && (
              <button
                onClick={() => onRemove(index)}
                className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition"
                title="Eliminar artículo"
              >
                <Trash2 size={18} />
              </button>
            )}
          </div>

          {/* Fila de cantidades */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <div>
              <label className="text-gray-600 text-xs block mb-1">Cantidad:</label>
              {isViewMode ? (
                <p className="font-bold text-xl text-blue-600">{articulo.cantidad}</p>
              ) : (
                <input
                  type="number"
                  min="1"
                  value={articulo.cantidad}
                  onChange={(e) => onUpdate(index, 'cantidad', parseInt(e.target.value) || 1)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-center font-bold text-blue-600"
                />
              )}
            </div>

            {!esServicio && !esExterno && (
              <>
                <div>
                  <label className="text-gray-600 text-xs block mb-1">Stock disponible:</label>
                  <p className={`font-bold text-xl ${
                    stockInsuficiente ? 'text-red-600' : stockBajo ? 'text-yellow-600' : 'text-green-600'
                  }`}>
                    {stockActual}
                  </p>
                </div>
                <div>
                  <label className="text-gray-600 text-xs block mb-1">Stock mínimo:</label>
                  <p className="font-semibold text-lg text-gray-700">{articulo.stock_minimo || 0}</p>
                </div>
                {stockInsuficiente && (
                  <div>
                    <label className="text-gray-600 text-xs block mb-1">Faltante:</label>
                    <p className="font-bold text-xl text-red-600">
                      {articulo.cantidad - stockActual}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* ── MODO EDICIÓN ──────────────────────────────── */}
          {!isViewMode && (
            <div className="grid grid-cols-2 gap-3 mt-3">

              {/* Estado */}
              <div>
                <label className="text-gray-600 text-xs block mb-1">Estado:</label>
                <select
                  value={articulo.status || 'solicitud_nueva'}
                  onChange={(e) => onUpdate(index, 'status', e.target.value)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="solicitud_nueva">Solicitud Nueva</option>
                  <option value="en_proceso">En Proceso</option>
                  <option value="completado">Completado</option>
                </select>
              </div>

              {/* Vía de entrega */}
              <div>
                <label className="text-gray-600 text-xs block mb-1">
                  Vía de entrega <span className="text-red-500">*</span>
                </label>
                <select
                  value={articulo.via || ''}
                  onChange={(e) => onUpdate(index, 'via', e.target.value)}
                  className={`w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500
                    ${!articulo.via
                      ? 'border-red-300 bg-red-50 text-gray-400'
                      : articulo.via === 'nacional'
                      ? 'border-green-400 bg-green-50 text-green-800 font-semibold'
                      : 'border-indigo-400 bg-indigo-50 text-indigo-800 font-semibold'
                    }
                  `}
                >
                  <option value="" disabled>— Seleccionar —</option>
                  <option value="nacional">🏠 Nacional</option>
                  <option value="internacional">✈️ Internacional</option>
                </select>
                {!articulo.via && (
                  <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle size={11} /> Campo requerido
                  </p>
                )}
              </div>

              {/* Fecha estimada */}
              <div>
                <label className="text-gray-600 text-xs block mb-1">Fecha estimada:</label>
                <input
                  type="date"
                  value={articulo.tiempo_entrega || ''}
                  onChange={(e) => onUpdate(index, 'tiempo_entrega', e.target.value)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Observación */}
              <div>
                <label className="text-gray-600 text-xs block mb-1">Observación:</label>
                <input
                  type="text"
                  value={articulo.observacion || ''}
                  onChange={(e) => onUpdate(index, 'observacion', e.target.value)}
                  placeholder="Notas adicionales"
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}

          {/* ── MODO VISTA ────────────────────────────────── */}
          {isViewMode && (
            <div className="grid grid-cols-3 gap-3 text-sm mt-3">
              <div>
                <span className="text-gray-500 block mb-1">Estado:</span>
                <p className="font-semibold text-gray-900">{articulo.status || 'Solicitud Nueva'}</p>
              </div>
              <div>
                <span className="text-gray-500 block mb-1">Vía de entrega:</span>
                {articulo.via === 'nacional' ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 border border-green-200 rounded-full text-xs font-semibold">
                    🏠 Nacional
                  </span>
                ) : articulo.via === 'internacional' ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-100 text-indigo-700 border border-indigo-200 rounded-full text-xs font-semibold">
                    ✈️ Internacional
                  </span>
                ) : (
                  <span className="text-gray-400 italic text-sm">Sin especificar</span>
                )}
              </div>
              <div>
                <span className="text-gray-500 block mb-1">Fecha estimada:</span>
                <p className="font-semibold text-gray-900">
                  {articulo.tiempo_entrega 
                    ? new Date(articulo.tiempo_entrega).toLocaleDateString('es-ES') 
                    : 'No definida'}
                </p>
              </div>
            </div>
          )}

          {isViewMode && articulo.observacion && (
            <div className="mt-3 p-3 bg-white rounded-lg border border-gray-200">
              <span className="text-gray-600 text-sm font-semibold block mb-1">💬 Observación:</span>
              <p className="text-sm text-gray-700">{articulo.observacion}</p>
            </div>
          )}

          {/* Alertas de stock / externo */}
          {esExterno ? (
            <div className="mt-3 p-3 bg-orange-100 border border-orange-300 rounded-lg flex items-center gap-2">
              <Package size={20} className="text-orange-600 flex-shrink-0" />
              <span className="text-sm text-orange-800 font-semibold">
                📦 Artículo externo — no tiene control de stock en el sistema
              </span>
            </div>
          ) : !esServicio && (
            <div className="mt-3">
              {stockInsuficiente ? (
                <div className="p-3 bg-red-100 border border-red-300 rounded-lg flex items-center gap-2">
                  <AlertCircle size={20} className="text-red-600 flex-shrink-0" />
                  <span className="text-sm text-red-800 font-semibold">
                    ⚠️ Stock insuficiente - Faltan {articulo.cantidad - stockActual} unidades
                  </span>
                </div>
              ) : stockBajo ? (
                <div className="p-3 bg-yellow-100 border border-yellow-300 rounded-lg flex items-center gap-2">
                  <AlertTriangle size={20} className="text-yellow-600 flex-shrink-0" />
                  <span className="text-sm text-yellow-800 font-semibold">
                    ⚡ Stock bajo - Se recomienda reabastecer
                  </span>
                </div>
              ) : (
                <div className="p-3 bg-green-100 border border-green-300 rounded-lg flex items-center gap-2">
                  <CheckCircle size={20} className="text-green-600 flex-shrink-0" />
                  <span className="text-sm text-green-800 font-semibold">
                    ✅ Stock disponible
                  </span>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default ArticuloItem;